import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import { SovosBox, SovosButton, SovosCircularProgress, SovosPopover, SovosTextField, SovosTreeList, SovosTypography } from "mosaic-react";
import { FirstPage, StackedLineChart } from "mosaic-react-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";

export const MainMenuContent = (props) => {
    const { menuProps: menuEntries, history, isCollapsed, setIsCollapsed, flatList = false, loading = false } = props || {}
    const { i18n } = useGlobalValue();
    const location = useLocation();
    const [menuEntryName, setMenuEntryName] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);
    const buttonRef = useRef(null);

    useEffect(() => {
        if (!isCollapsed) return;
        const savedPopoverState = localStorage.getItem("mainMenuPopoverOpen");
        const shouldBeOpen = savedPopoverState ? JSON.parse(savedPopoverState) : false;
        if (shouldBeOpen && buttonRef.current) {
            setAnchorEl(buttonRef.current);
        }
    }, [isCollapsed]);

    const handleOpenPopover = (e) => {
        setAnchorEl(e.currentTarget);
        localStorage.setItem("mainMenuPopoverOpen", JSON.stringify(true));
    };

    const handleClosePopover = () => {
        setAnchorEl(null);
        localStorage.setItem("mainMenuPopoverOpen", JSON.stringify(false));
    };

    // If flatList is true, flatten all nestedLinks to the first level
    const processedMenuEntries = useMemo(() => {
        if (!flatList) {
            return menuEntries || [];
        }

        // Flatten: take all nestedLinks and transform them into first-level items
        const flattened = [];
        (menuEntries || []).forEach((parentItem) => {
            if (parentItem.nestedLinks && parentItem.nestedLinks.length > 0) {
                parentItem.nestedLinks.forEach((nestedItem) => {
                    flattened.push({
                        ...nestedItem,
                        id: nestedItem.id || `${parentItem.id}_${nestedItem.content}`,
                        content: nestedItem.content || nestedItem.label,
                        label: nestedItem.label || nestedItem.content,
                    });
                });
            } else {
                // If there are no nestedLinks, keep the parent item
                flattened.push(parentItem);
            }
        });
        return flattened;
    }, [menuEntries, flatList]);

    const treeListMenu = processedMenuEntries.reduce((acc, listItem, index) => {
        if (!acc[index].content) {
            acc[index].content = <div>{i18n`${listItem.label}`}</div>;
        }
        // If flatList, don't add nestedItems (everything at first level) - undefined to hide the arrow
        if (flatList) {
            acc[index] = { ...acc[index] };
            // Don't add nestedItems when flatList, to hide the arrow
            delete acc[index].nestedItems;
        } else {
            acc[index] = { ...acc[index], nestedItems: listItem.nestedLinks || [] };
        }
        delete acc[index].nestedLinks;
        return acc;
        // Shallow copy in order not to delete the nestedLinks in main object, which could lead to empty menu on refresh
    }, [...processedMenuEntries.map(item => ({ ...item }))])

    const filteredTreeListMenu = menuEntryName ? treeListMenu.filter((menuItem) => {
        const filterText = menuEntryName?.toLowerCase().trim();
        const trimmedLabel = menuItem?.label?.toLowerCase().trim();
        if (menuEntryName &&
            ((trimmedLabel.startsWith(filterText) || trimmedLabel.includes(filterText)) ||
                (menuItem?.nestedItems || []).some((elem) => {
                    const trimmedContent = elem?.content?.toLowerCase().trim();
                    return trimmedContent.startsWith(filterText) || trimmedContent.includes(filterText)
                }))
        ) {
            return menuItem;
        }
        return false;
    }) : treeListMenu;

    const { initialSelectedItem, matchedParentId } = useMemo(() => {
        let selectedItem = "";
        let parentId = null;

        if (flatList) {
            // For flatList, search directly in first-level items
            const matchingItem = filteredTreeListMenu.find((menuItem) => {
                if (!menuItem?.to) return false;
                const menuPath = menuItem.to.split('?')[0];
                const currentPath = location?.pathname;
                return currentPath === menuPath || currentPath.startsWith(menuPath + '/');
            });
            if (matchingItem) {
                selectedItem = matchingItem.id;
            }
        } else {
            // Original logic for hierarchical structure
            (filteredTreeListMenu || []).forEach((menuItem) => {
                const matchingLink = (menuItem.nestedItems || []).find(
                    (linkItem) => {
                        if (!linkItem?.to) return false;
                        // Strip any query params from the link's `to` before comparing with pathname
                        const menuPath = linkItem.to.split('?')[0].replace(/\/+$/, '');
                        const currentPath = location?.pathname?.replace(/\/+$/, '');

                        return currentPath === menuPath || currentPath.startsWith(menuPath + '/');
                    }
                );
                if (matchingLink) {
                    selectedItem = matchingLink.id;
                    parentId = menuItem.id;
                }
            });
        }

        return { initialSelectedItem: selectedItem, matchedParentId: parentId };
    }, [filteredTreeListMenu, location?.pathname, flatList]);

    const initialExpandedItems = matchedParentId ? [matchedParentId] : [""];

    const menuContentProps = {
        i18n,
        menuEntryName,
        setMenuEntryName,
        filteredTreeListMenu,
        flatList,
        initialSelectedItem,
        location,
        initialExpandedItems,
        history,
        loading
    };

    return (
        <SovosBox sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflowY: "auto",
            backgroundColor: "#F5FAFF",
        }}>
            {!isCollapsed ?
                (menuContent(menuContentProps))
                :
                (mainMenuPopOver({ buttonRef, anchorEl, handleOpenPopover, handleClosePopover, menuContentProps }))
            }

            <footer style={{
                display: "flex",
                width: "100%",
                marginTop: "auto", // Forces itself to go to the bottom of the div
            }}>
                <SovosBox sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: isCollapsed ? "center" : "flex-end",
                    flexDirection: "row",
                    width: "100%",
                    height: "48px",
                    padding: '8px 16px',
                    borderTop: '1px solid #e0e0e0',
                    backgroundColor: "#E3ECF8",
                }}>
                    <SovosButton
                        aria-label={isCollapsed ? "Expand" : "Collapse"}
                        size="small"
                        variant="text"
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        sx={{
                            transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.7s',
                            height: '32px',
                            width: '32px',
                            minWidth: '32px',
                        }}
                        children={<FirstPage sx={{ color: 'action.active' }} />}
                    />
                </SovosBox>
            </footer>
        </SovosBox >
    );
};


export const menuContent = ({ i18n, menuEntryName, setMenuEntryName, filteredTreeListMenu, flatList, initialSelectedItem, location, initialExpandedItems, history, loading }) => {
    return (
        <SovosBox
            sx={{
                opacity: 1,
                transition: "opacity 0.7s ease-in-out",
                visibility: "visible",
                overflowY: "auto",
                padding: "16px",
                flex: 1,
            }}
        >
            <SovosTextField
                label={i18n`Menu Entry Name`}
                placeholder={i18n`Enter a Menu Entry Name`}
                value={menuEntryName}
                onChange={(e) => setMenuEntryName(e.target.value)}
                sx={{
                    marginBottom: "16px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    "& .MuiOutlinedInput-root": {
                        backgroundColor: "#FFFFFF",
                        width: "100%",
                    },
                }}
                variant="outlined"
            />
            {filteredTreeListMenu.length > 0 ?
                <SovosBox
                    sx={flatList ? {
                        // Hide all expand arrows/icons
                        '& .MuiTreeItem-iconContainer': {
                            display: 'none !important',
                        },
                        '& [class*="iconContainer"]': {
                            display: 'none !important',
                        },
                        '& [class*="expandIcon"]': {
                            display: 'none !important',
                        },
                        '& svg[class*="expand"]': {
                            display: 'none !important',
                        },
                        // Adjust padding when there's no arrow
                        '& .MuiTreeItem-content': {
                            paddingLeft: '8px !important',
                        },
                        '& [class*="content"]': {
                            paddingLeft: '8px !important',
                        }
                    } : {}}
                >
                    <SovosTreeList
                        key={initialSelectedItem || location?.pathname}
                        data={filteredTreeListMenu}
                        initialExpandedItems={initialExpandedItems}
                        initialSelectedItem={initialSelectedItem}
                        onItemClick={(item) => item?.to && history?.push(`${item.to}`)}
                    />
                </SovosBox> : loading ? (
                    <SovosBox sx={{ display: "flex", justifyContent: "center", padding: "24px" }}>
                        <SovosCircularProgress aria-label="Loading" />
                    </SovosBox>
                ) : (
                    <SovosBox>
                        <SovosTypography sx={{ textAlign: "center" }}>
                            {i18n`No results found`}
                        </SovosTypography>
                    </SovosBox>
                )
            }
        </SovosBox>)
}

export const mainMenuPopOver = ({ buttonRef, anchorEl, handleOpenPopover, handleClosePopover, menuContentProps }) => (
    <SovosBox sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginTop: "20px"
    }}>
        <SovosButton
            ref={buttonRef}
            sx={{
                height: '32px',
                width: '32px',
                minWidth: '32px',
            }}
            size="small"
            variant="text"
            onClick={handleOpenPopover}
            children={<StackedLineChart sx={{ color: 'action.active' }} />}
        />
        <SovosPopover
            sx={{ maxHeight: "50%" }}
            data-testid="menuCollapsedPopover"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClosePopover}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
        >
            {menuContent(menuContentProps)}
        </SovosPopover>
    </SovosBox>
);